import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_ID = "dj-turnstile-script";
const SCRIPT_SRC =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise = null;

function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (turnstileScriptPromise) return turnstileScriptPromise;

    turnstileScriptPromise = new Promise((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID);
        const script = existing ?? document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            turnstileScriptPromise = null;
            reject(new Error("Failed to load Cloudflare Turnstile"));
        };
        if (!existing) document.head.appendChild(script);
    });

    return turnstileScriptPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget(_, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const tokenRef = useRef("");
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    useEffect(() => {
        if (!siteKey) return undefined;

        let cancelled = false;

        loadTurnstileScript()
            .then(() => {
                if (
                    cancelled ||
                    !containerRef.current ||
                    !window.turnstile ||
                    widgetIdRef.current
                ) {
                    return;
                }

                widgetIdRef.current = window.turnstile.render(
                    containerRef.current,
                    {
                        sitekey: siteKey,
                        action: "signup",
                        callback: (token) => {
                            tokenRef.current = token;
                        },
                        "expired-callback": () => {
                            tokenRef.current = "";
                        },
                        "error-callback": () => {
                            tokenRef.current = "";
                        },
                    }
                );
            })
            .catch((error) => {
                console.error(error);
            });

        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
            tokenRef.current = "";
        };
    }, [siteKey]);

    useImperativeHandle(ref, () => ({
        getToken: () => tokenRef.current,
        reset: () => {
            tokenRef.current = "";
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
            }
        },
    }));

    if (!siteKey) {
        if (process.env.NODE_ENV === "development") {
            return (
                <p className="text-xs text-gray-500">
                    Cloudflare verification is not configured for local
                    development.
                </p>
            );
        }
        return (
            <p className="text-sm text-red-600">
                Verification is unavailable. Please try again later.
            </p>
        );
    }

    return <div ref={containerRef} className="flex justify-center" />;
});

export default TurnstileWidget;
